import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import QueryStatsIcon from "@mui/icons-material/QueryStats";

import { GroupAdminInfo } from "../../api/groups";
import { useGroupsStore } from "../../store/groupsStore";
import { useDialogState } from "../../hooks/useDialogState";
import { useSnackbar } from "../../hooks/useSnackbar";
import { AddMemberDialog } from "./AddMemberDialog";
import { GroupStatusDialog } from "./GroupStatusDialog";

interface GroupDetailDialogProps {
  open: boolean;
  group: GroupAdminInfo | undefined;
  onClose: () => void;
}

export const GroupDetailDialog: React.FC<GroupDetailDialogProps> = ({
  open,
  group,
  onClose,
}) => {
  const { groups, deleteGroup, removeMember, loadGroupDetail } =
    useGroupsStore();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [removingMemberId, setRemovingMemberId] = React.useState<string>();
  const [membersLoading, setMembersLoading] = React.useState(false);
  const addMemberDialog = useDialogState();
  const statusDialog = useDialogState();
  const { showSnackbar, SnackbarNode } = useSnackbar();

  // The list roster is lightweight (count + name). On open, fetch the enriched
  // group (email + account state) so the member table is complete.
  const groupId = group?.id;
  React.useEffect(() => {
    if (!open || !groupId) return;
    setMembersLoading(true);
    loadGroupDetail(groupId)
      .catch(() => {
        /* keep the lightweight roster already shown */
      })
      .finally(() => setMembersLoading(false));
  }, [open, groupId, loadGroupDetail]);

  // Always reflect latest store data for this group so member changes show immediately
  const currentGroup = groups.find((g) => g.id === group?.id) ?? group;

  if (!currentGroup) return null;

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteGroup(currentGroup.id);
      setDeleteConfirmOpen(false);
      onClose();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to delete group.",
        "error"
      );
      setDeleteLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    setRemovingMemberId(userId);
    try {
      await removeMember(currentGroup.id, userId);
      showSnackbar("Member removed.");
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to remove member.",
        "error"
      );
    } finally {
      setRemovingMemberId(undefined);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {currentGroup.name}
            </Typography>
            <Chip label={`#${currentGroup.tag}`} size="small" />
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Metadata */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2">{currentGroup.description}</Typography>
            {currentGroup.logoUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Logo URL
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {currentGroup.logoUrl}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Members */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Members ({currentGroup.members.length})
            </Typography>
            <Button
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={addMemberDialog.openDialog}
            >
              Add Member
            </Button>
          </Box>

          {membersLoading && currentGroup.members.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : currentGroup.members.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ py: 2, textAlign: "center" }}
            >
              No members yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {currentGroup.members.map((member) => {
                  const memberId = member.fsId ?? member.id;
                  const isRemoving = removingMemberId === memberId;
                  const isConfirmed = member.accountState === "CONFIRMED";
                  return (
                    <TableRow key={member.id}>
                      <TableCell>{member.displayName || "—"}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={member.accountState}
                          size="small"
                          sx={{
                            backgroundColor: isConfirmed ? "success.light" : "error.light",
                            color: isConfirmed ? "success.dark" : "error.dark",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove from group">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isRemoving}
                              onClick={() => handleRemoveMember(memberId)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete Group
            </Button>
            <Button
              variant="outlined"
              startIcon={<QueryStatsIcon />}
              onClick={statusDialog.openDialog}
            >
              View Status
            </Button>
          </Box>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleteLoading && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Group?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete{" "}
            <strong>{currentGroup.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <AddMemberDialog
        open={addMemberDialog.open}
        groupId={currentGroup.id}
        onClose={addMemberDialog.close}
      />

      <GroupStatusDialog
        open={statusDialog.open}
        groupId={currentGroup.id}
        groupName={currentGroup.name}
        onClose={statusDialog.close}
      />

      {SnackbarNode}
    </>
  );
};
